import { colors } from 'quasar';
import type { MapLayerDefinition } from '@/components/map/map.types';

export const DEFAULT_POINT_RADIUS = 4;

const markerSizePresets: Record<string, number> = { small: 4, medium: 8, large: 12 };

export function normalizeMarkerSize(value: unknown): number | undefined {
  if (value == null) return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized in markerSizePresets) {
      return markerSizePresets[normalized];
    }
    const parsed = Number.parseFloat(normalized);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return DEFAULT_POINT_RADIUS;
}

export function normalizeToFeatureCollection(gj: any) {
  if (!gj) return { type: 'FeatureCollection', features: [] };
  if (gj.type === 'FeatureCollection') return gj;
  if (gj.type === 'Feature') return { type: 'FeatureCollection', features: [gj] };
  if (typeof gj.type === 'string' && Array.isArray(gj.coordinates))
    return { type: 'FeatureCollection', features: [{ type: 'Feature', geometry: gj, properties: {} }] };
  return { type: 'FeatureCollection', features: [] };
}

function numberWithFallback(property: string, ...fallbacks: (string | number)[]) {
  const candidates = fallbacks.map((fallback) => (typeof fallback === 'string' ? ['get', fallback] : fallback));
  return ['to-number', ['coalesce', ['get', property], ...candidates]];
}

export function overlaySourceId(id: number | string) {
  return `custom-layer-${id}`;
}

export function overlayCircleId(id: number | string) {
  return `custom-layer-${id}-circle`;
}

export function overlayLineId(id: number | string) {
  return `custom-layer-${id}-line`;
}

export function overlayFillId(id: number | string) {
  return `custom-layer-${id}-fill`;
}

export function overlayBorderId(id: number | string) {
  return `custom-layer-${id}-border`;
}

export function overlayLayerIds(id: number | string): string[] {
  return [overlayLineId(id), overlayFillId(id), overlayBorderId(id), overlayCircleId(id)];
}

export async function loadAndPrepareGeoJson(urlOrObject: string | object) {
  try {
    const data =
      typeof urlOrObject === 'string' ? await fetch(urlOrObject).then((r) => (r.ok ? r.json() : null)) : urlOrObject;
    if (!data) return null;
    const fc = normalizeToFeatureCollection(data);
    fc.features = (fc.features || []).map((f: any) => {
      if (!f || !f.geometry) return f;
      const geomType = f.geometry.type;
      const props = f.properties || {};

      if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
        if (!props.stroke) {
          const base = props.fill || props.color || '#1976d2';
          try {
            props.stroke = colors.lighten(base, -20);
          } catch {
            props.stroke = base;
          }
        }
        if (props['stroke-width'] == null) props['stroke-width'] = 2;
        if (props['stroke-opacity'] == null) props['stroke-opacity'] = 1.0;
        if (props['fill-opacity'] == null) props['fill-opacity'] = props.opacity ?? 0.3;
      } else if (geomType === 'LineString' || geomType === 'MultiLineString') {
        if (props['stroke-width'] == null) props['stroke-width'] = 2;
        if (props['stroke-opacity'] == null) props['stroke-opacity'] = 0.9;
        if (!props.stroke && props.color) {
          props.stroke = props.color;
        }
      } else if (geomType === 'Point' || geomType === 'MultiPoint') {
        if (!props['marker-color']) {
          props['marker-color'] = props.color || props.stroke || '#1976d2';
        }
        if (props['marker-opacity'] == null) {
          props['marker-opacity'] = props.opacity ?? 0.95;
        }
        const normalizedSize =
          normalizeMarkerSize(props['marker-size']) ?? normalizeMarkerSize(props.radius) ?? DEFAULT_POINT_RADIUS;
        props['marker-size'] = normalizedSize;
        if (props['marker-stroke'] == null) {
          props['marker-stroke'] = props.stroke || '#0f4aa2';
        }
        if (props['marker-stroke-width'] == null) {
          props['marker-stroke-width'] = 1.5;
        }
      }

      f.properties = props;
      return f;
    });
    return fc;
  } catch (e) {
    console.debug('loadAndPrepareGeoJson error', e);
    return null;
  }
}

export async function addGeoJsonOverlay(map: any, layer: MapLayerDefinition, beneathLayerId?: string) {
  if (!map || !layer || !layer.file?.url) return;
  const sid = overlaySourceId(layer.id);
  const data = await loadAndPrepareGeoJson(layer.file.url);
  if (!data) return;
  if (!map.getSource(sid)) {
    map.addSource(sid, { type: 'geojson', data });
  } else {
    const src = map.getSource(sid);
    if (src && src.setData) src.setData(data);
  }
  const visible = layer.defaultVisibility === false ? 'none' : 'visible';
  const before = beneathLayerId && map.getLayer(beneathLayerId) ? beneathLayerId : undefined;

  if (!map.getLayer(overlayLineId(layer.id))) {
    map.addLayer(
      {
        id: overlayLineId(layer.id),
        type: 'line',
        source: sid,
        filter: ['any', ['==', ['geometry-type'], 'LineString'], ['==', ['geometry-type'], 'MultiLineString']],
        paint: {
          'line-width': numberWithFallback('stroke-width', 2),
          'line-color': ['coalesce', ['get', 'stroke'], ['get', 'color'], '#1976d2'],
          'line-opacity': numberWithFallback('stroke-opacity', 'opacity', 0.9),
        },
        layout: { visibility: visible },
      },
      before,
    );
  }

  if (!map.getLayer(overlayFillId(layer.id))) {
    map.addLayer(
      {
        id: overlayFillId(layer.id),
        type: 'fill',
        source: sid,
        filter: ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']],
        paint: {
          'fill-color': ['coalesce', ['get', 'fill'], ['get', 'color'], '#1976d2'],
          'fill-opacity': numberWithFallback('fill-opacity', 'opacity', 0.3),
        },
        layout: { visibility: visible },
      },
      before,
    );
  }

  if (!map.getLayer(overlayBorderId(layer.id))) {
    map.addLayer(
      {
        id: overlayBorderId(layer.id),
        type: 'line',
        source: sid,
        filter: ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']],
        paint: {
          'line-width': numberWithFallback('stroke-width', 2),
          'line-color': ['coalesce', ['get', 'stroke'], ['get', 'fill'], ['get', 'color'], '#0f4aa2'],
          'line-opacity': numberWithFallback('stroke-opacity', 'opacity', 1.0),
        },
        layout: { visibility: visible },
      },
      before,
    );
  }

  if (!map.getLayer(overlayCircleId(layer.id))) {
    map.addLayer(
      {
        id: overlayCircleId(layer.id),
        type: 'circle',
        source: sid,
        filter: ['any', ['==', ['geometry-type'], 'Point'], ['==', ['geometry-type'], 'MultiPoint']],
        paint: {
          'circle-radius': numberWithFallback('marker-size', DEFAULT_POINT_RADIUS),
          'circle-color': ['coalesce', ['get', 'marker-color'], ['get', 'color'], ['get', 'stroke'], '#1976d2'],
          'circle-opacity': numberWithFallback('marker-opacity', 'opacity', 0.95),
          'circle-stroke-color': ['coalesce', ['get', 'marker-stroke'], ['get', 'stroke'], '#0f4aa2'],
          'circle-stroke-width': numberWithFallback('marker-stroke-width', 1.5),
        },
        layout: { visibility: visible },
      },
      before,
    );
  }
}

export function removeGeoJsonOverlay(map: any, layerId: number | string) {
  if (!map) return;
  for (const id of overlayLayerIds(layerId)) {
    if (map.getLayer(id)) {
      map.removeLayer(id);
    }
  }
  const sid = overlaySourceId(layerId);
  if (map.getSource(sid)) {
    map.removeSource(sid);
  }
}

export function setGeoJsonOverlayVisibility(map: any, layerId: number | string, visible: boolean) {
  if (!map) return;
  for (const id of overlayLayerIds(layerId)) {
    if (map.getLayer(id)) {
      map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
    }
  }
}
